import { Link, Navigate } from "react-router-dom";

import { PortalContent } from "layouts/PortalLayout";
import Loading from "components/Loading";
import { useUser } from "context/UserContext";
import HomeSections from "components/HomeSections";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { homeBody, singleRecordTarget, type MeHome } from "helpers/me-home";
import { useMeHome } from "hooks/useMeHome";
import PageHeader from "components/PageHeader";

function LoadFailed() {
  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 text-center shadow-sm">
      <div className="font-serif italic text-soil">
        We couldn’t load your account. Please refresh the page.
      </div>
    </div>
  );
}

/** The way back to the lead form, on Home whatever else it lists. */
function GetStarted({ hasListings }: { hasListings: boolean }) {
  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
      <h2 className="font-serif text-xl text-soil">
        {hasListings ? "Need help with something else?" : "How can we help?"}
      </h2>
      <p className="mt-1 text-base text-oat-700">
        Answer a few questions and we’ll take it from there.
      </p>
      <Link to="/start" className={`${CONTINUE_BUTTON_CLASS} mt-4`}>
        Get started
      </Link>
    </div>
  );
}

function HomeBody({ home, failed }: { home: MeHome | null; failed: boolean }) {
  const body = homeBody(home, failed);
  if (body === "failed" || home === null) return <LoadFailed />;
  if (body === "get_started") return <GetStarted hasListings={false} />;
  return (
    <>
      <HomeSections home={home} />
      <GetStarted hasListings />
    </>
  );
}

export default function Home() {
  const { user, loading: userLoading } = useUser();
  const { home, isLoading: homeLoading, failed } = useMeHome();

  if (userLoading || homeLoading) {
    return (
      <PortalContent>
        <Loading message="Loading..." />
      </PortalContent>
    );
  }

  const target = singleRecordTarget(home, homeLoading, failed);
  if (target !== null) return <Navigate to={target} replace />;

  return (
    <PortalContent className="space-y-6">
      <PageHeader
        title={
          <>
            <i>Welcome back</i>
            {user?.first_name && (
              <>
                ,{" "}
                <span className="font-sans font-bold text-evergreen">
                  {user.first_name}
                </span>
              </>
            )}
          </>
        }
      />

      <HomeBody home={home} failed={failed} />
    </PortalContent>
  );
}
